import { Fragment, useEffect, useRef, useState } from "react";
import { Ban, CheckCircle2, CreditCard, Eye, LifeBuoy, Loader2, MapPin, Pencil } from "lucide-react";
import { useT52 } from "@/i18n/v52";
import { Dash, PlanChip } from "@/components/v52/Bits";

export const GROUPS = [
  ["kd_g_master", ["overview", "firma", "standorte", "hours"]],
  ["kd_g_content", ["services", "news", "items", "media", "inquiries", "reviews"]],
  ["kd_g_eval", ["stats", "website"]],
  ["kd_g_business", ["plan", "billing"]],
  ["kd_g_admin", ["team", "support", "security", "storage"]],
];

const METRICS = [
  ["kd_m_locations", "locations", "org-metric-locations"], ["kd_m_users", "members", "org-metric-users"],
  ["kd_m_items", "items_total", "org-metric-items"], ["kd_m_inq", "inquiries_total", "org-metric-inquiries"], ["kd_m_media", "media_count", "org-metric-media"],
];

export function KdHead({ org, stats, name, active, isSuper, onGoto, onCockpit, onLock }) {
  const { t52, tOr } = useT52();
  const status = String(org.status || "").toLowerCase();
  const sec = (key, Icon, label) => (
    <button type="button" className="f52-btn f52-btn-secondary" onClick={() => onGoto(key)} data-testid={`org-${key === "firma" ? "edit" : key}-button`}><Icon aria-hidden="true" />{t52(label)}</button>
  );
  return (
    <section className="f52-kd-head" aria-labelledby="kd-name">
      <div>
        <h1 id="kd-name" data-testid="org-detail-name">{name}</h1>
        <p className="f52-tid" data-testid="org-detail-tenant-id">{org.tenant_id}</p>
        <div className="f52-kd-chips" data-testid="org-detail-chips">
          <PlanChip plan={org.plan} />
          <span className={`f52-kd-chip${status === "suspended" ? " bad" : active ? " ok" : ""}`} data-testid="org-detail-status">{org.status ? tOr(`orgst_${org.status}`, org.status) : `${t52("f_status")} —`}</span>
          <span className="f52-kd-chip">{t52(org.account_mode === "multi" ? "kd_multi" : "kd_single")}</span>
          <span className="f52-kd-chip">{t52("kd_account", { v: org.customer_status ? tOr(`cs_${org.customer_status}`, org.customer_status) : "—" })}</span>
          <span className="f52-kd-chip"><MapPin aria-hidden="true" />{t52("kd_locs", { n: stats.locations ?? "—" })}</span>
        </div>
      </div>
      {isSuper && (
        <div className="f52-kd-actions" role="group" aria-label={t52("kd_actionsAria")}>
          <button type="button" className="f52-btn f52-btn-primary" onClick={onCockpit} data-testid="org-open-cockpit-button"><Eye aria-hidden="true" />{t52("kd_cockpit")}</button>
          {sec("firma", Pencil, "kd_edit")}
          {sec("plan", CreditCard, "kd_plan")}
          {sec("support", LifeBuoy, "kd_support")}
          <button type="button" className={`f52-btn ${active ? "f52-btn-danger" : "f52-btn-ok"}`} onClick={onLock} data-testid="org-toggle-button">
            {active ? <Ban aria-hidden="true" /> : <CheckCircle2 aria-hidden="true" />}{t52(active ? "kd_lock" : "kd_unlock")}
          </button>
        </div>
      )}
    </section>
  );
}

export function KdMetrics({ stats }) {
  const { t52 } = useT52();
  return (
    <dl className="f52-vals five f52-kd-metrics" data-testid="org-metrics">
      {METRICS.map(([label, key, tid]) => <div className="f52-val raised" key={key} data-testid={tid}><dt>{t52(label)}</dt><dd><Dash v={stats[key]} /></dd></div>)}
    </dl>
  );
}

export function KdNav({ tab, onTab }) {
  const { t52 } = useT52();
  return (
    <nav className="f52-panel f52-kd-nav" aria-label={t52("kd_areas")} data-testid="org-tabs">
      {GROUPS.map(([g, tabs]) => (
        <Fragment key={g}>
          <h3>{t52(g)}</h3>
          <ul>
            {tabs.map((k) => (
              <li key={k}><button type="button" className="f52-kd-tab" aria-current={tab === k ? "true" : "false"} onClick={() => onTab(k)} data-testid={`org-tab-${k}`}>{t52(`kd_tab_${k}`)}</button></li>
            ))}
          </ul>
        </Fragment>
      ))}
    </nav>
  );
}

export function KdSelect({ tab, onTab }) {
  const { t52 } = useT52();
  return (
    <div className="f52-kd-select">
      <label htmlFor="kd-sel">{t52("kd_area")}</label>
      <select id="kd-sel" value={tab} onChange={(e) => onTab(e.target.value)} data-testid="org-tab-select">
        {GROUPS.map(([g, tabs]) => <optgroup key={g} label={t52(g)}>{tabs.map((k) => <option key={k} value={k}>{t52(`kd_tab_${k}`)}</option>)}</optgroup>)}
      </select>
    </div>
  );
}

// Bestätigungsdialog für Sperren/Aktivieren: Fokus im Dialog, Esc bricht ab, Fokus zurück.
export function LockDialog({ name, active, onCancel, onConfirm }) {
  const { t52 } = useT52();
  const ref = useRef(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const prev = document.activeElement;
    ref.current?.querySelector("[data-dlg-cancel]")?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); cancelRef.current(); return; }
      if (e.key !== "Tab" || !ref.current) return;
      const f = [...ref.current.querySelectorAll("button:not([disabled])")];
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); prev?.focus?.(); };
  }, []);

  const confirm = async () => { setBusy(true); await onConfirm(); setBusy(false); };
  return (
    <div ref={ref} className="f52-dlg" role="alertdialog" aria-modal="true" aria-labelledby="kd-dlg-t" aria-describedby="kd-dlg-d"
      data-testid="org-lock-dialog" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="box">
        <h2 id="kd-dlg-t">{t52(active ? "kd_lockT" : "kd_unlockT")}</h2>
        <p id="kd-dlg-d">{t52(active ? "kd_lockD" : "kd_unlockD", { name })}</p>
        <div className="acts">
          <button type="button" className="f52-btn f52-btn-secondary" data-dlg-cancel onClick={onCancel} data-testid="org-lock-cancel">{t52("kd_cancel")}</button>
          <button type="button" className={`f52-btn ${active ? "f52-btn-danger-solid" : "f52-btn-ok-solid"}`} disabled={busy} onClick={confirm} data-testid="org-lock-confirm">
            {busy ? <Loader2 aria-hidden="true" className="f52-spin" /> : active ? <Ban aria-hidden="true" /> : <CheckCircle2 aria-hidden="true" />}
            {t52(active ? "kd_lockC" : "kd_unlockC")}
          </button>
        </div>
      </div>
    </div>
  );
}
